import React from 'react'
import { Well } from '@pushly/aqe/lib/components'
import { Form } from '@ant-design/compatible'
import '@ant-design/compatible/assets/index.css'
import { Select } from 'antd'
import { CatalogItemType } from '@pushly/models/lib/enums'
import { TriggerType } from '@pushly/models/lib/enums/journeys'
import { getPluralForCatalogItemType } from '@pushly/models/lib/enums/catalogs/catalog-item-type'
import {
    TriggerGroupId,
    TRIGGER_SUPPORT_BY_CATALOG_ITEM_TYPE,
    TRIGGER_SUPPORT_BY_NON_CATALOG_TRIGGER_TYPE,
} from '@pushly/models/lib/enums/journeys/trigger-type.enum'


export interface ITriggerSectionProps {
    trigger: {
        getConfiguration?: () => { type?: TriggerType; params?: Record<string, unknown> }
    }
    hasAbandonedViewAccess: boolean
    hasSavedItemsAccess: boolean
    domainItemType?: CatalogItemType
    onChange: (next: TriggerType) => void
}

type AccessFlags = Pick<ITriggerSectionProps, 'hasAbandonedViewAccess' | 'hasSavedItemsAccess'>

/**
 * Represents metadata for a trigger group.
 *
 * This interface defines the structure for metadata associated with
 * a trigger group, including its unique identifier, default label,
 * and precedence.
 *
 * Properties:
 * - `id`: A unique identifier for the trigger group.
 * - `defaultLabel`: The default display label for the trigger group.
 * - `precedence`: An integer representing the precedence of the group,
 *   used to determine its ordering relative to other groups.
 */
interface ITriggerGroupMeta {
    id: TriggerGroupId
    defaultLabel: string
    precedence: number
}

/**
 * A record representing the available trigger groups, where each key corresponds to a `TriggerGroupId`
 * and each value provides metadata information about a specific group.
 */
const TRIGGER_GROUPS: Record<TriggerGroupId, ITriggerGroupMeta> = {
    push: { id: 'push', defaultLabel: 'Push notifications', precedence: 0 },
    ecommerce: { id: 'ecommerce', defaultLabel: 'E‑commerce', precedence: 1 },
    recipes: { id: 'recipes', defaultLabel: 'Recipes', precedence: 2 },
}

/**
 * Retrieves all trigger groups, sorted in ascending order based on their precedence.
 *
 * @return {ITriggerGroupMeta[]} A sorted array of trigger group metadata objects.
 */
function getAllTriggerGroupsOrdered(): ITriggerGroupMeta[] {
    return Object.values(TRIGGER_GROUPS).sort((a, b) => a.precedence - b.precedence)
}

/**
 * Returns the human-readable, sentence-style label that completes:
 * "Subscribers enter this campaign after ..."
 */
function getSentenceLabel(tt: TriggerType, itemType: CatalogItemType): string {
    const plural = getPluralForCatalogItemType(itemType)
    switch (tt) {
        case TriggerType.SUBSCRIBED:
            return 'subscribing to push notifications'
        case TriggerType.ABANDONED_CART:
            return `adding ${plural} to their cart and leaving the site`
        case TriggerType.ABANDONED_VIEW:
            return `browsing ${plural} and leaving the site`
        case TriggerType.SAVED_ITEM:
            return `saving or wishlisting ${plural}`
        default:
            return '' // not rendered
    }
}

/**
 * Decides if a trigger is allowed by UI-level feature flags.
 * Shared metadata does not include tenant/feature logic, by design.
 */
function isAllowedByFlags(tt: TriggerType, flags: AccessFlags): boolean {
    switch (tt) {
        case TriggerType.ABANDONED_VIEW:
            return flags.hasAbandonedViewAccess
        case TriggerType.SAVED_ITEM:
            return flags.hasSavedItemsAccess
        default:
            return true
    }
}

/**
 * Builds grouped <Select> options for the given item type:
 * - Catalog-driven triggers come from the shared support map.
 * - Non-catalog triggers come from the shared non-catalog registry.
 * - Groups (labels and order) come from shared TRIGGER_GROUPS metadata.
 *
 * Keep this function UI-only: it composes domain metadata with feature flags and presentation text.
 */
function buildOptionGroups(
    itemType: CatalogItemType,
    flags: AccessFlags,
): Array<{ label: string; options: Array<{ value: TriggerType; label: string }> }> {
    // 1) Collect catalog-based triggers for this item type
    const catalogSupport = TRIGGER_SUPPORT_BY_CATALOG_ITEM_TYPE[itemType] ?? {}
    const catalogEntriesList = Object.entries(catalogSupport) as Array<[TriggerType, { group: TriggerGroupId }]>

    const catalogEntries = catalogEntriesList.reduce<
        Array<{ groupId: TriggerGroupId; value: TriggerType; label: string }>
    >((acc, [trigger, meta]) => {
        if (!isAllowedByFlags(trigger, flags)) return acc
        const text = getSentenceLabel(trigger, itemType)
        if (!text) return acc
        acc.push({ groupId: meta.group, value: trigger, label: text })
        return acc
    }, [])

    // 2) Merge shared non-catalog triggers (e.g., SUBSCRIBED under 'push')
    const nonCatalogEntriesList = Object.entries(TRIGGER_SUPPORT_BY_NON_CATALOG_TRIGGER_TYPE) as Array<
        [TriggerType, { group: TriggerGroupId }]
    >

    const nonCatalogEntries = nonCatalogEntriesList.reduce<
        Array<{ groupId: TriggerGroupId; value: TriggerType; label: string }>
    >((acc, [trigger, meta]) => {
        const text = getSentenceLabel(trigger, itemType)
        if (!text) return acc
        acc.push({ groupId: meta.group, value: trigger, label: text })
        return acc
    }, [])

    const allEntries = [...catalogEntries, ...nonCatalogEntries]

    // 3) Group options by group id using shared group metadata for label and order
    const groupsIndex = getAllTriggerGroupsOrdered().reduce<Record<string, number>>((idx, g, i) => {
        idx[g.id] = i
        return idx
    }, {})

    const groupedById = new Map<
        TriggerGroupId,
        { id: TriggerGroupId; options: Array<{ value: TriggerType; label: string }> }
    >()

    for (const entry of allEntries) {
        if (!groupedById.has(entry.groupId)) {
            groupedById.set(entry.groupId, { id: entry.groupId, options: [] })
        }
        groupedById.get(entry.groupId)!.options.push({ value: entry.value, label: entry.label })
    }

    // 4) Convert to display shape (label from shared defaults) and sort by shared precedence
    return Array.from(groupedById.values())
        .sort((a, b) => (groupsIndex[a.id] ?? 999) - (groupsIndex[b.id] ?? 999))
        .map((g) => ({
            label: TRIGGER_GROUPS[g.id]?.defaultLabel ?? g.id,
            options: g.options,
        }))
}

export const TriggerSection: React.FC<ITriggerSectionProps> = React.memo(
    ({ trigger, hasAbandonedViewAccess, hasSavedItemsAccess, onChange, domainItemType }) => {
        const cfg = (trigger?.getConfiguration?.() ?? {}) as { type?: TriggerType; params?: Record<string, unknown> }
        const triggerType = cfg?.type as TriggerType | undefined
        const rawItemType = (cfg?.params as any)?.item_type
        const itemType = (() => {
            // Prefer item type set on journey campaign trigger
            if (rawItemType) {
                const rawStr = String(rawItemType).toLowerCase()
                const enumValues = Object.values(CatalogItemType) as Array<string>
                const direct = enumValues.find((v) => String(v).toLowerCase() === rawStr)
                if (direct) return direct as unknown as CatalogItemType
                const map: Record<string, CatalogItemType | undefined> = {
                    product: CatalogItemType.PRODUCT,
                    event: CatalogItemType.TICKETED_EVENT,
                    recipe: CatalogItemType.RECIPE,
                }
                return map[rawStr] ?? (domainItemType ?? CatalogItemType.PRODUCT)
            }

            // 2) Fallback to domain item type, if provided
            if (domainItemType) return domainItemType

            // 3) Final fallback to PRODUCT
            return CatalogItemType.PRODUCT
        })()


        const groups = buildOptionGroups(itemType, { hasAbandonedViewAccess, hasSavedItemsAccess })

        return (
            <Well mode="ghost" title="Trigger" hideFooter={true} className="entry-cap-well">
                <Form.Item
                    className="campaign-trigger-editor-label"
                    label="Subscribers enter this campaign after"
                    colon={false}
                >
                    <Select
                        className="campaign-trigger-editor-type-selection"
                        popupClassName="campaign-trigger-editor-type-dropdown"
                        size="small"
                        placeholder="Select a campaign trigger"
                        value={triggerType}
                        onChange={onChange as any}
                        style={{ minWidth: 250 }}
                        dropdownMatchSelectWidth={false}
                        dropdownStyle={{ minWidth: 300 }}
                    >
                        {groups.map((g) => (
                            <Select.OptGroup key={g.label} label={g.label}>
                                {g.options.map((o) => (
                                    <Select.Option key={o.value} value={o.value}>
                                        {o.label}
                                    </Select.Option>
                                ))}
                            </Select.OptGroup>
                        ))}
                    </Select>
                </Form.Item>
            </Well>
        )
    },
)

export default TriggerSection
